import { captureKey } from "../../shared/captures";
import { make } from "../dom";
import { languageLabel, treatmentLabel, type ReviewPrinting } from "./catalogue";
import { printingImageSource } from "./printing-image";
import "./comparison.css";

export type ComparisonCandidate = { printing: ReviewPrinting; score: number };

interface ComparisonOptions {
  scanSource: string | null;
  previewOnly: boolean;
  candidates: ComparisonCandidate[];
  selectedKey: string;
  onChoose: (printing: ReviewPrinting) => void;
}

interface ImagePane {
  element: HTMLElement;
  viewport: HTMLElement;
  image: HTMLImageElement;
  setSource: (source: string | null, description: string) => void;
}

function candidateKey(printing: ReviewPrinting): string {
  return `${printing.scryfall || captureKey(printing)}:${printing.face}`;
}

/** One explicit choice, with the same normalized crop applied to both card images. */
export function mountComparison(options: ComparisonOptions): {
  element: HTMLElement;
  update: (
    scanSource: string | null,
    previewOnly: boolean,
    candidates: ComparisonCandidate[],
  ) => void;
  updateMedia: (scanSource: string | null, previewOnly: boolean) => void;
  destroy: () => void;
} {
  const lifetime = new AbortController();
  const { signal } = lifetime;
  let candidates = options.candidates;
  let selectedIndex = Math.max(
    0,
    candidates.findIndex(({ printing }) => candidateKey(printing) === options.selectedKey),
  );
  let zoom = 1;
  let centerX = 0.5;
  let centerY = 0.5;
  let disposed = false;

  const element = make("section", "review-comparison");
  element.setAttribute("aria-label", "Compare card printings");
  const body = make("div", "review-comparison-body");
  body.dataset.scroll = "comparison";
  const navigation = make("div", "review-comparison-navigation");

  function button(text: string, focus: string, action: () => void): HTMLButtonElement {
    const node = make("button", "review-comparison-button", text);
    node.type = "button";
    node.dataset.focus = `comparison-${focus}`;
    node.addEventListener("click", action, { signal });
    return node;
  }

  const previous = button("←", "previous", () => moveCandidate(-1));
  previous.setAttribute("aria-label", "Previous candidate");
  const next = button("→", "next", () => moveCandidate(1));
  next.setAttribute("aria-label", "Next candidate");
  const position = make("span", "review-comparison-position");
  position.setAttribute("aria-live", "polite");
  position.setAttribute("aria-atomic", "true");
  navigation.append(previous, position, next);
  const title = make("h2", "review-comparison-title");

  function imagePane(label: string, catalogue: boolean): ImagePane {
    const element = make("figure", "review-comparison-pane");
    const caption = make("figcaption", "review-comparison-caption", label);
    const viewport = make("div", "review-comparison-viewport");
    viewport.tabIndex = 0;
    viewport.dataset.focus = catalogue ? "comparison-catalogue" : "comparison-scan";
    viewport.setAttribute("role", "group");
    viewport.setAttribute(
      "aria-label",
      `${label}. Drag to move both images. Use plus or minus to zoom, arrow keys to pan, Home for full card, End for bottom edge.`,
    );
    viewport.setAttribute(
      "aria-keyshortcuts",
      "+ - ArrowLeft ArrowRight ArrowUp ArrowDown Home End",
    );
    const image = make("img", "review-comparison-image");
    image.draggable = false;
    image.decoding = "async";
    if (catalogue) image.crossOrigin = "anonymous";
    const state = make("span", "review-comparison-image-state");
    state.setAttribute("role", "status");
    viewport.append(image, state);
    element.append(caption, viewport);
    let currentSource: string | null | undefined;

    const setSource = (source: string | null, description: string): void => {
      if (image.alt !== description) image.alt = description;
      if (source === currentSource) return;
      currentSource = source;
      image.hidden = true;
      state.hidden = false;
      viewport.dataset.imageState = source ? "loading" : "unavailable";
      state.textContent = source
        ? "Loading image…"
        : catalogue
          ? "Scryfall image unavailable"
          : "Scan image unavailable";
      image.onload = null;
      image.onerror = null;
      if (!source) {
        image.removeAttribute("src");
        return;
      }
      image.onload = () => {
        if (disposed || currentSource !== source) return;
        image.hidden = false;
        state.hidden = true;
        viewport.dataset.imageState = "ready";
      };
      image.onerror = () => {
        if (disposed || currentSource !== source) return;
        image.hidden = true;
        state.hidden = false;
        state.textContent = catalogue ? "Scryfall image unavailable" : "Scan image unavailable";
        viewport.dataset.imageState = "unavailable";
      };
      image.src = source;
    };
    return { element, viewport, image, setSource };
  }

  const images = make("div", "review-comparison-images");
  const scan = imagePane("Your scan", false);
  const catalogue = imagePane("Scryfall", true);
  images.append(scan.element, catalogue.element);
  const previewNote = make(
    "p",
    "review-comparison-note",
    "Saved preview · fine details may be limited",
  );
  const controls = make("div", "review-comparison-controls");
  controls.setAttribute("aria-label", "Shared image zoom");
  const full = button("Full card", "full", () => setView(1, 0.5, 0.5));
  const bottom = button("Bottom edge", "bottom", () => setView(3, 0, 1));
  const minus = button("−", "zoom-out", () => setView(zoom / 1.5, centerX, centerY));
  minus.setAttribute("aria-label", "Zoom out both images");
  const plus = button("+", "zoom-in", () => setView(zoom * 1.5, centerX, centerY));
  plus.setAttribute("aria-label", "Zoom in both images");
  const zoomLabel = make("output", "review-comparison-zoom", "1×");
  zoomLabel.setAttribute("aria-label", "Image magnification");
  const zoomButtons = make("div", "review-comparison-zoom-buttons");
  zoomButtons.append(minus, zoomLabel, plus);
  controls.append(full, bottom, zoomButtons);
  const hint = make(
    "p",
    "review-comparison-note",
    "Zoom and drag either image to compare the same detail.",
  );

  const details = make("dl", "review-comparison-details");
  const differenceNote = make("p", "review-comparison-difference-note");
  const footer = make("div", "review-comparison-footer");
  const choose = button("Choose printing", "choose", () => {
    const printing = candidates[selectedIndex]?.printing;
    if (printing) options.onChoose(printing);
  });
  choose.classList.add("review-comparison-choose");
  footer.append(choose);
  body.append(navigation, title, images, previewNote, controls, hint, details, differenceNote);
  element.append(body, footer);

  function setView(nextZoom: number, nextX: number, nextY: number): void {
    const focused = document.activeElement;
    zoom = Math.max(1, Math.min(6, nextZoom));
    const bound = 0.5 / zoom;
    centerX = Math.max(bound, Math.min(1 - bound, nextX));
    centerY = Math.max(bound, Math.min(1 - bound, nextY));
    const transform = `translate(${(0.5 - centerX * zoom) * 100}%, ${(0.5 - centerY * zoom) * 100}%) scale(${zoom})`;
    for (const pane of [scan, catalogue]) {
      pane.image.style.transform = transform;
      pane.viewport.dataset.zoomed = String(zoom > 1);
    }
    element.dataset.zoom = zoom.toFixed(2);
    zoomLabel.textContent = `${Number(zoom.toFixed(1))}×`;
    minus.disabled = zoom <= 1;
    plus.disabled = zoom >= 6;
    if ((focused === minus && minus.disabled) || (focused === plus && plus.disabled)) {
      full.focus({ preventScroll: true });
    }
    full.setAttribute("aria-pressed", String(zoom === 1));
    bottom.setAttribute(
      "aria-pressed",
      String(
        Math.abs(zoom - 3) < 0.001 &&
          Math.abs(centerY - (1 - bound)) < 0.001 &&
          Math.abs(centerX - bound) < 0.001,
      ),
    );
  }

  function installGestures(viewport: HTMLElement): void {
    type Point = { x: number; y: number };
    const pointers = new Map<number, Point>();
    let start = { x: 0, y: 0, distance: 0, zoom: 1, centerX: 0.5, centerY: 0.5 };
    const point = (event: PointerEvent): Point => {
      const bounds = viewport.getBoundingClientRect();
      return {
        x: (event.clientX - bounds.left) / Math.max(1, bounds.width),
        y: (event.clientY - bounds.top) / Math.max(1, bounds.height),
      };
    };
    const measure = (): { x: number; y: number; distance: number } => {
      const points = [...pointers.values()];
      if (points.length < 2) return { ...(points[0] ?? { x: 0, y: 0 }), distance: 0 };
      const [a, b] = points;
      return {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        distance: Math.hypot(a.x - b.x, a.y - b.y),
      };
    };
    const begin = (): void => {
      start = { ...measure(), zoom, centerX, centerY };
    };
    viewport.addEventListener(
      "pointerdown",
      (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        event.preventDefault();
        viewport.focus({ preventScroll: true });
        pointers.set(event.pointerId, point(event));
        viewport.setPointerCapture(event.pointerId);
        viewport.dataset.dragging = "true";
        begin();
      },
      { signal },
    );
    viewport.addEventListener(
      "pointermove",
      (event) => {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, point(event));
        const current = measure();
        const nextZoom = Math.max(
          1,
          Math.min(
            6,
            start.distance > 0 ? start.zoom * (current.distance / start.distance) : start.zoom,
          ),
        );
        const anchorX = start.centerX + (start.x - 0.5) / start.zoom;
        const anchorY = start.centerY + (start.y - 0.5) / start.zoom;
        setView(
          nextZoom,
          anchorX - (current.x - 0.5) / nextZoom,
          anchorY - (current.y - 0.5) / nextZoom,
        );
      },
      { signal },
    );
    const end = (event: PointerEvent): void => {
      pointers.delete(event.pointerId);
      viewport.dataset.dragging = String(pointers.size > 0);
      begin();
    };
    viewport.addEventListener("pointerup", end, { signal });
    viewport.addEventListener("pointercancel", end, { signal });
    viewport.addEventListener("lostpointercapture", end, { signal });
    viewport.addEventListener(
      "keydown",
      (event) => {
        const step = 0.12 / zoom;
        switch (event.key) {
          case "+":
          case "=":
            setView(zoom * 1.5, centerX, centerY);
            break;
          case "-":
          case "_":
            setView(zoom / 1.5, centerX, centerY);
            break;
          case "ArrowLeft":
            setView(zoom, centerX - step, centerY);
            break;
          case "ArrowRight":
            setView(zoom, centerX + step, centerY);
            break;
          case "ArrowUp":
            setView(zoom, centerX, centerY - step);
            break;
          case "ArrowDown":
            setView(zoom, centerX, centerY + step);
            break;
          case "Home":
            setView(1, 0.5, 0.5);
            break;
          case "End":
            setView(3, 0, 1);
            break;
          default:
            return;
        }
        event.preventDefault();
        event.stopPropagation();
      },
      { signal },
    );
    viewport.addEventListener(
      "wheel",
      (event) => {
        // Ordinary scrolling still reaches the details; modified wheel is explicit zoom.
        if (!event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        setView(zoom * Math.exp(-event.deltaY * 0.01), centerX, centerY);
      },
      { signal, passive: false },
    );
  }

  installGestures(scan.viewport);
  installGestures(catalogue.viewport);

  function renderCandidate(): void {
    const focused = document.activeElement;
    const printing = candidates[selectedIndex]?.printing;
    previous.disabled = selectedIndex <= 0;
    next.disabled = selectedIndex >= candidates.length - 1;
    choose.disabled = !printing;
    if (
      (focused === previous && previous.disabled) ||
      (focused === next && next.disabled) ||
      (focused === choose && choose.disabled)
    ) {
      catalogue.viewport.focus({ preventScroll: true });
    }
    details.replaceChildren();
    if (!printing) {
      element.dataset.selectedKey = "";
      position.textContent = "No candidates";
      title.textContent = "Printing unavailable";
      catalogue.setSource(null, "Scryfall card image");
      choose.textContent = "Choose printing";
      differenceNote.hidden = true;
      return;
    }
    element.dataset.selectedKey = candidateKey(printing);
    position.textContent = `Candidate ${selectedIndex + 1} of ${candidates.length}`;
    title.textContent = printing.name;
    catalogue.setSource(
      printingImageSource(printing, "normal"),
      `${printing.name}, ${printing.setName}, ${printing.setCode.toUpperCase()} #${printing.collector}`,
    );
    const shortLabel = `${printing.setCode.toUpperCase()} #${printing.collector}`;
    choose.textContent = `Choose ${shortLabel}`;
    choose.setAttribute(
      "aria-label",
      `Choose ${printing.name}, ${shortLabel}, ${treatmentLabel(printing)}`,
    );
    const fields: Array<{
      name: string;
      value: (printing: ReviewPrinting) => string;
      key: (printing: ReviewPrinting) => string | undefined;
    }> = [
      {
        name: "Set",
        value: (p) => `${p.setName || p.setCode.toUpperCase()} · ${p.setCode.toUpperCase()}`,
        key: (p) => p.setCode.toLowerCase(),
      },
      { name: "Collector number", value: (p) => `#${p.collector}`, key: (p) => p.collector },
      {
        name: "Treatment",
        value: treatmentLabel,
        key: (p) => {
          const treatment = treatmentLabel(p);
          return treatment === "frame details unavailable" ? undefined : treatment;
        },
      },
    ];
    if (candidates.some(({ printing }) => printing.language)) {
      const language = (p: ReviewPrinting): string =>
        p.language === "en" ? "English" : languageLabel(p.language) || "Unknown";
      fields.push({ name: "Language", value: language, key: (p) => p.language });
    }
    let differenceCount = 0;
    for (const field of fields) {
      const differs =
        new Set(candidates.map(({ printing }) => field.key(printing)).filter(Boolean)).size > 1;
      const row = make("div", "review-comparison-field");
      row.dataset.differs = String(differs);
      const label = make("dt", "", field.name);
      if (differs) {
        label.append(make("span", "review-comparison-differs", "differs"));
        differenceCount++;
      }
      row.append(label, make("dd", "", field.value(printing)));
      details.append(row);
    }
    differenceNote.hidden = candidates.length < 2;
    differenceNote.textContent = differenceCount
      ? "Marked details differ between these candidates. Check them against your scan."
      : "No confirmed differences in the listed details. Compare artwork and printed text.";
  }

  function moveCandidate(direction: number): void {
    const nextIndex = Math.max(0, Math.min(candidates.length - 1, selectedIndex + direction));
    if (nextIndex === selectedIndex) return;
    selectedIndex = nextIndex;
    // Keep the shared crop so another candidate can be checked at the same detail.
    renderCandidate();
  }

  function update(
    scanSource: string | null,
    previewOnly: boolean,
    nextCandidates: ComparisonCandidate[],
  ): void {
    if (disposed) return;
    const key = candidates[selectedIndex] && candidateKey(candidates[selectedIndex].printing);
    const nextIndex = nextCandidates.findIndex(({ printing }) => candidateKey(printing) === key);
    candidates = nextCandidates;
    selectedIndex =
      nextIndex >= 0 ? nextIndex : Math.max(0, Math.min(selectedIndex, candidates.length - 1));
    updateMedia(scanSource, previewOnly);
    renderCandidate();
  }

  function updateMedia(scanSource: string | null, previewOnly: boolean): void {
    if (disposed) return;
    scan.setSource(scanSource, "Your photographed card");
    const hidden = !previewOnly || !scanSource;
    if (previewNote.hidden !== hidden) previewNote.hidden = hidden;
  }

  setView(1, 0.5, 0.5);
  update(options.scanSource, options.previewOnly, options.candidates);
  return {
    element,
    update,
    updateMedia,
    destroy: () => {
      disposed = true;
      lifetime.abort();
      for (const pane of [scan, catalogue]) {
        pane.image.onload = null;
        pane.image.onerror = null;
      }
    },
  };
}
